/**
 * Behaviour for the Shopify localization (country / language) form.
 *
 * This lives in a once-loaded asset rather than in inline scripts inside
 * snippets/localization-form.liquid for two reasons:
 *
 *  1. The form is rendered inside `.mobile-navigation-drawer-template`, a
 *     `<script type="text/template">` element. A nested script tag would
 *     terminate that template early and destroy the whole mobile navigation.
 *  2. The drawer markup is cloned and then passed through
 *     `theme.suffixIds(drawer, 'MobileNav')` (assets/main.js), which rewrites
 *     every id in the subtree. Nothing here may look a form up by id.
 *
 * Every handler below is therefore delegated from `document` and scoped by
 * class/attribute, so it works for any number of forms in any placement —
 * header, mobile drawer, footer — without per-form initialisation.
 */
(() => {
  // The header and the footer each load this asset when their own selector is
  // enabled, and a duplicate <script src> executes twice. Without this guard a
  // second delegated listener would submit the form twice.
  if (window.themeLocalizationFormLoaded) return;
  window.themeLocalizationFormLoaded = true;

  const FORM_SELECTOR = 'form.localization';
  const SELECTOR_SELECTOR = '.localization__selector';

  /**
   * Shopify bakes `return_to` into the form at server render time. This theme
   * soft-navigates via assets/prefetch-accelerator.js, which replaces only
   * `#content > div` and leaves the header and footer in place, so a form
   * rendered on the landing page would still carry that page's path several
   * navigations later and drop the buyer back there after switching currency.
   * Re-point it at the live URL immediately before submitting.
   *
   * Only ever a same-origin relative path, never a full URL.
   * @param {HTMLFormElement} form
   */
  function refreshReturnTo(form) {
    const path = window.location.pathname + window.location.search;
    let input = form.querySelector('input[name="return_to"]');
    if (!input) {
      input = document.createElement('input');
      input.type = 'hidden';
      input.name = 'return_to';
      form.appendChild(input);
    }
    input.value = path;
  }

  /**
   * Records that the buyer has chosen a market themselves. Read by
   * assets/localization-recommendation.js, which must not keep recommending a
   * switch to someone who has already used the selector - including someone who
   * accepted the banner and then switched back.
   */
  function markMarketChosen() {
    try {
      window.localStorage.setItem('theme:localization-chosen', '1');
    } catch (e) {
      /* storage denied; the banner falls back to its own dismissal handling */
    }
  }

  /**
   * Everything that must happen immediately before a localization form is sent.
   * @param {HTMLFormElement} form
   */
  function prepareSubmit(form) {
    refreshReturnTo(form);
    markMarketChosen();
  }

  // `form.submit()` does not fire a submit event, so the change handler below
  // calls refreshReturnTo() directly. This listener covers the paths that DO
  // fire one: the recommendation banner's accept button, and the <noscript>
  // form's Update button on a page where JS later loads.
  document.addEventListener('submit', (evt) => {
    const form = evt.target;
    if (form && form.matches && form.matches(FORM_SELECTOR)) {
      prepareSubmit(form);
    }
  });

  /**
   * A localization change is always a form POST followed by a full document
   * navigation. The market-namespaced sessionStorage caches in
   * collection-grid.js and patternid-sort.js depend on that: their old-market
   * entries become unreachable because the key changes, and they age out on
   * their existing expiries. Never mutate localization in place.
   */
  document.addEventListener('change', (evt) => {
    const selector = evt.target.closest ? evt.target.closest(SELECTOR_SELECTOR) : null;
    if (!selector) return;

    const form = selector.closest(FORM_SELECTOR);
    if (!form) return;

    const input = selector.querySelector('input[type="hidden"]');
    if (!input) return;

    // `detail.selectedValue` is set by the custom-select element; a native
    // <select> (the <noscript> path) dispatches a plain change event and needs
    // the value read off the target instead.
    const value = evt.detail ? evt.detail.selectedValue : evt.target.value;
    if (!value) return;

    input.value = value;
    prepareSubmit(form);
    form.submit();
  });

  /**
   * `country-selector` lazy-loads the full country list on first open, so the
   * header does not carry every market's markup on every page.
   */
  customElements.whenDefined('custom-select').then(() => {
    if (customElements.get('country-selector')) return;

    class CountrySelector extends customElements.get('custom-select') {
      constructor() {
        super();
        this.loaded = false;
      }

      async showListbox() {
        if (this.loaded) {
          super.showListbox();
          return;
        }

        // A rapid second click/tap while the list is loading must not fire a
        // second request; the in-flight call opens the listbox when it settles.
        if (this.loadingCountries) return;
        this.loadingCountries = true;

        const container = this.closest(SELECTOR_SELECTOR);
        const url = container && container.dataset.countriesUrl;
        if (!url) {
          this.loadingCountries = false;
          super.showListbox();
          return;
        }

        this.button.classList.add('is-loading');
        this.button.setAttribute('aria-disabled', 'true');

        try {
          const response = await fetch(url);
          if (!response.ok) throw new Error(response.status);

          const tmpl = document.createElement('template');
          tmpl.innerHTML = await response.text();

          const el = tmpl.content.querySelector('.custom-select__listbox');
          if (!el) throw new Error('No listbox in country-selector section');
          this.listbox.innerHTML = el.innerHTML;

          this.options = this.querySelectorAll('.custom-select__option');

          this.popular = this.querySelectorAll('[data-popular]');
          if (this.popular.length) {
            this.popular[this.popular.length - 1].closest('.custom-select__option')
              .classList.add('custom-select__option--visual-group-end');
          }

          this.selectedOption = this.querySelector('[aria-selected="true"]');
          if (!this.selectedOption) {
            this.selectedOption = this.listbox.firstElementChild;
          }

          this.loaded = true;
        } catch {
          const message = (container && container.dataset.countriesError) || '';
          this.listbox.textContent = '';
          const li = document.createElement('li');
          li.textContent = message;
          this.listbox.appendChild(li);
        } finally {
          this.loadingCountries = false;
          super.showListbox();
          this.button.classList.remove('is-loading');
          this.button.setAttribute('aria-disabled', 'false');
        }
      }

      // The button label is a country name of unpredictable length; letting the
      // base class pin a pixel width causes it to jump on selection.
      setButtonWidth() {}
    }

    customElements.define('country-selector', CountrySelector);
  });
})();
